import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Disclosure } from '@/components/ui/Disclosure'
import { QUOTE_ARCHIVE_LIMIT, useQuoteArchive } from '@/features/chat/api/useQuoteArchive'
import { useFormat } from '@/i18n/useFormat'

/* The source quote's conversation, shut, above the case chat (S-1, owner
 * decision O3).
 *
 * Operations used to meet a case with no idea what was promised on the way to
 * it: the quote's thread — the route the client asked for, the rate's expiry,
 * the caveat somebody typed — stayed on a quote they usually cannot open. The
 * server now serves that thread on the case, to whoever may read the case's
 * chat, and this is where it is read.
 *
 * Shut by default, because the case's own conversation is what the dock is for
 * and an archive opened over it would push it off a laptop screen. Read-only,
 * because the thread is frozen: there is no composer, and nothing here posts,
 * marks read or subscribes. Absent — not empty — when there is nothing to
 * show: a case with no quote, a 404 (no source quote, or a reader who may not
 * read the case chat), a quote nobody wrote on, and while the read is pending
 * or failed: the dock's own conversation is the thing to fall back to, and a
 * block that appeared to say "no archive" would be a claim nobody can check.
 *
 * The quote is LINKED only when `quoteReachable` says this reader may open it.
 * Operations on the case usually may not, and a link that opens a 404 is a
 * promise the page cannot keep; the number is still said, as text.
 *
 * Mentions are ids and are not resolved here: the archive's authors are the
 * quote's team, whose names this panel does not hold, so the body is printed
 * exactly as it was written. Quote conversations carry no files.
 */
export function QuoteArchiveBlock({
  caseId,
  quoteId,
  enabled,
  className,
}: {
  caseId: string
  /** The case's source quote, when it has one. No quote, no read. */
  quoteId?: string
  /** Whether this reader may use the case chat AND the dock is on screen —
   *  the host's answer, so a collapsed dock asks nothing. */
  enabled: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const archive = useQuoteArchive(caseId, Boolean(quoteId) && enabled)

  if (!quoteId || !archive.isSuccess || archive.data.messages.length === 0) return null
  const { messages, quoteNumber, quoteReachable, hasMore } = archive.data

  return (
    <Disclosure
      summary={t('cases.chat.archive.title')}
      count={messages.length}
      className={className}
      bodyClassName="asa-scroll flex max-h-64 flex-col gap-3 overflow-y-auto"
    >
      <div className="text-text-muted flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-[12px]">
        <span className="font-light">{t('cases.chat.archive.readOnly')}</span>
        {quoteReachable ? (
          <Link
            to={`/quotes/${archive.data.quoteId}`}
            className="text-text-body hover:text-text-heading asa-num underline underline-offset-2"
          >
            {t('cases.chat.archive.openQuote', { number: quoteNumber })}
          </Link>
        ) : (
          <span className="asa-num">{t('cases.chat.archive.quote', { number: quoteNumber })}</span>
        )}
      </div>

      {hasMore ? (
        <p className="text-text-muted text-[12px] font-light">
          {t('cases.chat.archive.truncated', { count: QUOTE_ARCHIVE_LIMIT })}
        </p>
      ) : null}

      <ol className="flex flex-col gap-2.5">
        {messages.map((message) => (
          <li key={message.id} className="flex flex-col gap-0.5">
            <div className="flex items-baseline gap-2 text-[12px]">
              <span className="text-text-heading truncate">{message.authorName}</span>
              <time
                dateTime={message.sentAt}
                className="text-text-faint asa-num shrink-0 text-[11px]"
              >
                {format.timestamp(message.sentAt)}
              </time>
              {message.edited && !message.deleted ? (
                <span className="text-text-faint text-[11px]">{t('chat.edited')}</span>
              ) : null}
            </div>
            {/* A tombstone's notice, never its body — which the server has
                already blanked — italic and muted so it reads as a gap. */}
            {message.deleted ? (
              <p className="text-text-faint text-[13px] italic">{t('chat.deletedBody')}</p>
            ) : (
              <p className="text-text-body text-[13px] leading-snug break-words whitespace-pre-wrap">
                {message.body}
              </p>
            )}
          </li>
        ))}
      </ol>
    </Disclosure>
  )
}
